import { useRef, useState } from 'react'
import { Box, Button, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ETLExample } from '../../../assets/etl/ETL_Example.svg'
import { ReactComponent as CSV } from '../../../assets/etl/CSV.svg'
import { ReactComponent as External } from '../../../assets/etl/External.svg'
import FsContainer from '../common/Container'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'
import { getIsDemoAccount } from '../../../utils'
import FsDemoAccountModal from '../../common/molecules/DemoAccountModal'

interface FsUploadProps {
  goNext: () => void
}

const FsDataSource = ({ goNext }: FsUploadProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)

  const isDemoAccount = getIsDemoAccount()
  const alertRef = useRef<FsAlertRef>(null)

  return (
    <Box>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
      {showInfo ? (
        <FsContainer
          footer={
            <Button variant="contained" onClick={goNext}>
              {t('continue')}
            </Button>
          }
        >
          <Typography variant="h3" className="font-semibold">
            {t('before.uploading')}
          </Typography>
          <Typography my={2}>{t('before.uploading.info')}</Typography>
          <ETLExample />
          <Typography mt={2} className="font-semibold">
            {t('most.common.errors')}
          </Typography>
          <Typography mt={1} component="div" sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography className="font-semibold" mr={0.5}>
              A.
            </Typography>
            {t('error.A')}
          </Typography>
          <Typography component="div" sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography className="font-semibold" mr={0.5}>
              B.
            </Typography>
            {t('error.B')}
          </Typography>
          <Typography component="div" sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography className="font-semibold" mr={0.5}>
              C.
            </Typography>
            {t('error.C')}
          </Typography>
          <Typography component="div" sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography className="font-semibold" mr={0.5}>
              D.
            </Typography>
            {t('error.D')}
          </Typography>
          {/* <Typography mt={2} fontWeight="bold">
            {`You can find this information in the Help Center > Video Tutorial > Uploading
            Data.`}
          </Typography> */}
        </FsContainer>
      ) : (
        <FsContainer>
          <Typography>{t('upload.sources')}</Typography>
          <div>
            <Box
              sx={{
                border: `2px solid ${theme.palette.action.selected}`,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              }}
              p={2}
              borderRadius={1}
              maxWidth="700px"
              mt={4}
              mb={2}
              onClick={() => (isDemoAccount ? setOpenDemoModal(true) : setShowInfo(true))}
            >
              <Typography className="font-semibold">{t('company.file')}</Typography>
              <Stack direction="row" p={2}>
                <Box mr={2}>
                  <CSV height={90} width={120} />
                </Box>
                <Box>
                  <Typography>{t('file.info')}</Typography>
                </Box>
              </Stack>
            </Box>
          </div>
          <a
            href="mailto:support.fs@ines-analytics.com"
            style={{
              color: theme.palette.text.primary,
              textDecoration: 'none',
              display: 'inline-block',
              width: 'fit-content',
            }}
          >
            <Box
              sx={{
                border: `2px solid ${theme.palette.action.selected}`,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              }}
              p={2}
              borderRadius={1}
              maxWidth="700px"
            >
              <Typography className="font-semibold">{t('external.source')}</Typography>
              <Stack direction="row" p={2}>
                <Box mr={2}>
                  <External height={90} width={120} />
                </Box>
                <Box>
                  <Typography>{t('external.source.info')}</Typography>
                  <Stack direction="row" alignItems="center" mt={1}>
                    <Typography mr={1}>{`${t('email')}: `}</Typography>
                    <TextField
                      disabled
                      value="support.fs@ines-analytics.com"
                      size="small"
                      sx={{ width: '370px' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              variant="contained"
                              color="secondary"
                              sx={{
                                mr: -1.6,
                                p: 1,
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                              }}
                              onClick={(event: any) => {
                                event.stopPropagation()
                                event.preventDefault()
                                navigator.clipboard.writeText('support.fs@ines-analytics.com')
                                alertRef.current?.openAlert({ message: t('copied') })
                              }}
                            >
                              {t('copy')}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </a>
        </FsContainer>
      )}
      <FsAlert ref={alertRef} />
    </Box>
  )
}

export default FsDataSource
